import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Receipt, DollarSign, Loader2, Download, AlertTriangle, Shield, TrendingUp, RotateCcw } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { formatBusinessDate, formatBusinessDateTime } from '../lib/dateTime';

const ReportsPage = () => {
  const { api, permissions, isOwner, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [salesReport, setSalesReport] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);
  const [period, setPeriod] = useState('month');
  const [reportRange, setReportRange] = useState('last_30_days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const canViewReports = isOwner || permissions?.view_reports === true;

  const rangeQuery = useMemo(() => {
    if (reportRange === 'custom') {
      if (!customStart || !customEnd) return null;
      return `range=custom&start=${customStart}&end=${customEnd}`;
    }
    return `range=${reportRange}`;
  }, [reportRange, customStart, customEnd]);

  const fetchData = useCallback(async () => {
    if (!canViewReports) {
      setLoading(false);
      return;
    }
    if (!rangeQuery) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [salesRes, taxRes] = await Promise.all([
        api.get(`/reports/sales?${rangeQuery}`).catch(() => ({ data: null })),
        api.get(`/reports/tax-summary?period=${period}`).catch(() => ({ data: null })),
      ]);
      setSalesReport(salesRes.data);
      setTaxSummary(taxRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [api, canViewReports, rangeQuery, period]);

  const downloadBlob = async (endpoint, filename) => {
    if (!rangeQuery) {
      toast.error('Select start and end date for custom range');
      return;
    }
    try {
      const response = await api.get(`${endpoint}?${rangeQuery}`, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Failed to download ${filename}`);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'UGX 0';
    return `UGX ${Number(amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!canViewReports) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="font-heading text-2xl font-semibold mb-2">Reports Access Required</h2>
        <p className="text-muted-foreground max-w-md">
          You need report permissions to view this page.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const dailyRows = salesReport?.dailyBreakdown || [];
  const topProducts = salesReport?.topProducts || [];
  const transactions = salesReport?.transactions || [];
  const paymentMethods = salesReport?.paymentMethods || [];
  const summary = salesReport?.summary;
  const profit = salesReport?.profit;
  const paymentBreakdown = salesReport?.payment_methods;
  const refunds = salesReport?.refunds;

  const avgDailySales = dailyRows.length > 0
    ? dailyRows.reduce((sum, row) => sum + (row.total_sales || 0), 0) / dailyRows.length
    : 0;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground">Sales, tax, and performance analytics</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={reportRange} onValueChange={setReportRange}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Report range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_7_days">Last 7 days</SelectItem>
              <SelectItem value="last_30_days">Last 30 days</SelectItem>
              <SelectItem value="last_60_days">Last 60 days</SelectItem>
              <SelectItem value="last_90_days">Last 90 days</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>
          {reportRange === 'custom' && (
            <>
              <Input
                type="date"
                className="w-40"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <Input
                type="date"
                className="w-40"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
            </>
          )}
          <Button variant="outline" onClick={() => downloadBlob('/reports/sales/export/csv', 'sales-report.csv')}>
            <Download className="mr-2 h-4 w-4" />
            CSV
          </Button>
          <Button variant="outline" onClick={() => downloadBlob('/reports/sales/pdf', 'survipos-sales-report.pdf')}>
            <Download className="mr-2 h-4 w-4" />
            PDF
          </Button>
          <Button variant="outline" onClick={() => downloadBlob('/reports/sales/csv/summary', 'sales-summary.csv')}>
            <Download className="mr-2 h-4 w-4" />
            Summary CSV
          </Button>
          <Button variant="outline" onClick={() => downloadBlob('/reports/sales/csv/transactions', 'sales-transactions.csv')}>
            <Download className="mr-2 h-4 w-4" />
            Txns CSV
          </Button>
          <Button variant="outline" onClick={() => downloadBlob('/reports/sales/csv/products', 'sales-products.csv')}>
            <Download className="mr-2 h-4 w-4" />
            Products CSV
          </Button>
        </div>
      </div>

      {salesReport && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Advanced Sales Summary</CardTitle>
            <CardDescription>Metrics for the selected range.</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Gross Sales</p>
              <p className="font-mono text-lg font-semibold">{formatCurrency(summary?.total_sales)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Net Sales</p>
              <p className="font-mono text-lg font-semibold">{formatCurrency(summary?.net_sales)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Discounts</p>
              <p className="font-mono text-lg font-semibold">{formatCurrency(summary?.discounts)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Gross Profit</p>
              <p className="font-mono text-lg font-semibold">{formatCurrency(profit?.gross_profit)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card shadow-sm">
          <CardHeader className="pb-2">
            <p className="text-sm text-muted-foreground">Total Sales</p>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold font-mono text-primary">{formatCurrency(summary?.total_sales)}</p>
          </CardContent>
        </Card>
        <Card className="stat-card shadow-sm">
          <CardHeader className="pb-2">
            <p className="text-sm text-muted-foreground">Avg Daily Sales</p>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold font-mono">{formatCurrency(avgDailySales)}</p>
          </CardContent>
        </Card>
        <Card className="stat-card shadow-sm">
          <CardHeader className="pb-2">
            <p className="text-sm text-muted-foreground">Transactions</p>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold font-mono">{(summary?.transactions || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="stat-card shadow-sm">
          <CardHeader className="pb-2">
            <p className="text-sm text-muted-foreground">VAT Collected</p>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold font-mono text-secondary">{formatCurrency(summary?.vat_collected)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" />Payment Mix</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm">Cash: <span className="font-mono">{formatCurrency(paymentBreakdown?.cash_total)}</span></p>
            <p className="text-sm">Mobile: <span className="font-mono">{formatCurrency(paymentBreakdown?.mobile_money_total)}</span></p>
            <p className="text-sm">Card: <span className="font-mono">{formatCurrency(paymentBreakdown?.card_total)}</span></p>
            <p className="text-sm">Credit: <span className="font-mono">{formatCurrency(paymentBreakdown?.credit_total)}</span></p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><RotateCcw className="h-4 w-4" />Refunds</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Total Refunded</p>
            <p className="text-2xl font-mono font-semibold">{formatCurrency(refunds?.total || 0)}</p>
            <p className="text-sm text-muted-foreground mt-1">Refund Count: {(refunds?.count || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">COGS</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-mono font-semibold">{formatCurrency(profit?.cogs)}</p>
            <p className="text-sm text-muted-foreground mt-1">Cost of goods sold</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Gross Margin</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-mono font-semibold">{Number(profit?.gross_margin || 0).toFixed(1)}%</p>
            <p className="text-sm text-muted-foreground mt-1">Profit efficiency</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales" data-testid="sales-tab">Sales Report</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tax-tab">Tax Report</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Daily Sales Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                {dailyRows.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyRows}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="date"
                        stroke="hsl(var(--muted-foreground))"
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        tickFormatter={(value) => formatBusinessDate(value, { day: '2-digit', month: '2-digit' })}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                        formatter={(value) => [formatCurrency(value)]}
                        labelFormatter={(value) => formatBusinessDate(value, { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}
                      />
                      <Bar dataKey="total_sales" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No sales data for this period
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Daily Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[380px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Gross</TableHead>
                      <TableHead>Net</TableHead>
                      <TableHead>Cash</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Card</TableHead>
                      <TableHead>Credit</TableHead>
                      <TableHead>VAT</TableHead>
                      <TableHead>Txns</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyRows.map((day) => (
                      <TableRow key={day.date}>
                        <TableCell className="font-medium">{formatBusinessDate(day.date, { weekday: 'short', day: '2-digit', month: 'short' })}</TableCell>
                        <TableCell className="font-mono font-semibold text-primary">{formatCurrency(day.total_sales)}</TableCell>
                        <TableCell className="font-mono">{formatCurrency(day.net_sales)}</TableCell>
                        <TableCell className="font-mono text-sm">{formatCurrency(day.total_cash)}</TableCell>
                        <TableCell className="font-mono text-sm">{formatCurrency(day.total_mobile_money)}</TableCell>
                        <TableCell className="font-mono text-sm">{formatCurrency(day.total_card)}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {day.total_credit > 0 ? <span className="text-destructive">{formatCurrency(day.total_credit)}</span> : formatCurrency(0)}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-secondary">{formatCurrency(day.total_vat)}</TableCell>
                        <TableCell>{day.transaction_count}</TableCell>
                      </TableRow>
                    ))}
                    {dailyRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12">
                          <Receipt className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                          <p className="text-muted-foreground">No sales data for this period</p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Top Products</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[320px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Net Sales</TableHead>
                        <TableHead>Profit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topProducts.map((row) => (
                        <TableRow key={row.product_id}>
                          <TableCell className="font-medium">{row.product_name}</TableCell>
                          <TableCell>{row.qty_sold}</TableCell>
                          <TableCell className="font-mono">{formatCurrency(row.net_sales)}</TableCell>
                          <TableCell className="font-mono">{row.profit === null ? 'N/A' : formatCurrency(row.profit)}</TableCell>
                        </TableRow>
                      ))}
                      {topProducts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No product sales data</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Recent Transactions</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[320px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Receipt</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Cashier</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((tx) => (
                        <TableRow key={tx.receipt_number}>
                          <TableCell className="font-mono text-xs">{tx.receipt_number}</TableCell>
                          <TableCell>{formatBusinessDateTime(tx.created_at, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</TableCell>
                          <TableCell className="font-mono">{formatCurrency(tx.total_amount)}</TableCell>
                          <TableCell className="capitalize">{(tx.payment_method || '').replaceAll('_', ' ')}</TableCell>
                          <TableCell>{tx.cashier_name}</TableCell>
                        </TableRow>
                      ))}
                      {transactions.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No transactions for selected range</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Payment Methods</CardTitle>
              <CardDescription>Breakdown across the selected range</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {paymentMethods.map((method) => (
                <div key={method.name} className="rounded-lg border border-border/60 p-3">
                  <p className="text-sm text-muted-foreground">{method.name}</p>
                  <p className="font-mono font-semibold">{formatCurrency(method.total)}</p>
                  <Badge variant="outline" className="mt-2">{Number(method.percentage || 0).toFixed(1)}%</Badge>
                </div>
              ))}
              {paymentMethods.length === 0 && (
                <div className="text-muted-foreground text-sm">No payment method data available.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tax" className="mt-4 space-y-6">
          <div className="flex items-center gap-4">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
                <SelectItem value="year">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {taxSummary ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="survival-glow border-primary/30 shadow-lg">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Receipt className="h-5 w-5" />
                    Tax Classification
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Tax Type</span>
                      <Badge variant={taxSummary.tax_type === 'vat_registered' ? 'default' : 'secondary'}>
                        {taxSummary.tax_type === 'vat_registered' ? 'VAT Registered' : 'Presumptive Tax'}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Period</span>
                      <span className="font-medium capitalize">{taxSummary.period}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Total Turnover</span>
                      <span className="font-mono font-bold text-primary">{formatCurrency(taxSummary.total_sales)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Transactions</span>
                      <span className="font-mono">{taxSummary.transaction_count}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Tax Liability
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {taxSummary.tax_type === 'vat_registered' ? (
                      <>
                        <div>
                          <p className="text-sm text-muted-foreground">VAT Collected (18%)</p>
                          <p className="text-3xl font-bold font-mono text-secondary">{formatCurrency(taxSummary.total_vat_collected)}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">File monthly VAT returns and remit collected VAT.</p>
                      </>
                    ) : (
                      <>
                        <div>
                          <p className="text-sm text-muted-foreground">Estimated Annual Tax</p>
                          <p className="text-3xl font-bold font-mono text-secondary">{formatCurrency(taxSummary.presumptive_tax_estimate)}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">Presumptive tax estimate based on current turnover.</p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {taxSummary.approaching_vat_threshold && (
                <Card className="lg:col-span-2 debt-glow border-warning/50 shadow-lg">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <AlertTriangle className="h-8 w-8 text-warning flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-lg mb-2">Approaching VAT Threshold</h3>
                        <p className="text-muted-foreground mb-2">
                          Your turnover is approaching UGX 150,000,000. VAT registration becomes mandatory after crossing the threshold.
                        </p>
                        <p className="text-sm">
                          Current turnover: <span className="font-mono font-bold">{formatCurrency(taxSummary.total_sales)}</span>
                          <br />
                          Threshold: <span className="font-mono">{formatCurrency(taxSummary.vat_threshold)}</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No tax data available. Start making sales to see your tax summary.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="text-center text-sm text-muted-foreground py-4">
        Powered by <span className="text-secondary font-medium">Kareem Data Labs</span> • URA-Compliant POS for Ugandan SMEs
      </div>
    </div>
  );
};

export default ReportsPage;
